// frontend/src/pages/Login.js
import React from 'react';
import { CssBaseline, Box, Card as MuiCard, Typography, Stack, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import MicrosoftLoginButton from '../components/MicrosoftLoginButton';
import { useAnalytics } from '../hooks/useAnalytics';
import { withPageTracking } from '../hooks/withPageTracking';
import BlueElephant from '../assets/images/Blue_elephant.png';

// Styled container (same as before)
const SignInContainer = styled(Stack)(({ theme }) => ({
  flex: 1,
  width: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  overflow: 'hidden',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
  },
}));

// Styled Card container (same as before)
const StyledCard = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  maxWidth: '850px',
  margin: 'auto',
  overflow: 'hidden',
  borderRadius: theme.spacing(2),
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
}));

// Left section with image (same as before)
const CardImage = styled(Box)(({ theme }) => ({
  flex: 1,
  background: `url(${BlueElephant}) no-repeat center center`,
  backgroundSize: 'cover',
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
  // Add ARIA role and label for accessibility
  role: 'img',
  'aria-label': 'University of Alabama elephant mascot illustration',
}));

// Right section for login form (now with Microsoft SSO button)
const CardContent = styled(Box)(({ theme }) => ({
  flex: 2,
  padding: theme.spacing(4),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

function Login() {
  // Use analytics hook
  const { trackLoginFailed } = useAnalytics();

  // Error handler for login failures
  const handleLoginError = (error) => {
    trackLoginFailed();
    console.error('Login failed:', error);
  };

  return (
    <>
      <CssBaseline />
      <SignInContainer>
        <StyledCard>
          {/* Left side with image */}
          <CardImage />

          {/* Right side with login content */}
          <CardContent>
            <Typography
              component="h1"
              variant="h4"
              sx={{ textAlign: 'center', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
            >
              Login
            </Typography>
            <MicrosoftLoginButton onLoginError={handleLoginError} />
            <Divider sx={{ marginY: 2 }}>or</Divider>
            <Typography sx={{ textAlign: 'center' }}>
              First time signing in? Your account will be created automatically.
            </Typography>
          </CardContent>
        </StyledCard>
      </SignInContainer>
    </>
  );
}

// Export with page tracking wrapper
export default withPageTracking(Login, 'login_page');
